'use client'

import { useEffect, useRef, useState } from 'react'
import * as m from 'motion/react-m'
import { MotionLayout } from '@/lib/motion/lazy-motion'

import ScrollView from '@/components/ui/ScrollView'
import Toggle from '@/components/ui/Toggle'

import { Icon } from '@/icons/Icon'
import { cn, scrollToSection } from '@/lib/utils'
import { usePageContext } from '@/components/content/common/PageContext'

type Tab = {
  icon: React.ReactNode
  text: string
  count?: number
  action?: () => void
  scrollOffset?: number
  disableScroll?: boolean
}

type TabToggleProps = {
  defaultValue?: string
  defaultIcon?: React.ReactNode
  deals?: number
  comparisonCount?: number
  center?: boolean
  scrollOffset?: number
  primary?: boolean
  tabs?: Tab[]
}

export default function TabToggle({
  defaultValue,
  defaultIcon,
  deals,
  comparisonCount,
  center = false,
  scrollOffset = 45,
  primary = false,
  tabs,
}: TabToggleProps) {
  const [width, setWidth] = useState(0)
  const { setPageTab } = usePageContext()

  // Determine the initial tab value
  const initialValue = defaultValue || (tabs && tabs.length > 0 ? tabs[0].text : '')
  const adjustedOffset = width > 768 ? scrollOffset : scrollOffset + 32

  // Set default value
  useEffect(() => {
    if (initialValue) {
      setPageTab(initialValue)
    }
  }, [initialValue, setPageTab])

  useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  // If custom tabs are provided, use them instead of default structure
  if (tabs && tabs.length > 0) {
    return (
      <Toggle center={center} className={cn(center && 'my-8 md:my-14', primary ? 'top-20' : '')}>
        <ScrollView className={cn('flex px-2 pt-2.5', center && 'justify-center')}>
          {tabs.map((tab, index) => (
            <ToggleOption
              key={index}
              icon={tab.icon}
              text={tab.text}
              count={tab.count}
              action={tab.action}
              scrollOffset={center ? 170 : adjustedOffset}
              disableScroll={tab.disableScroll}
            />
          ))}
        </ScrollView>
      </Toggle>
    )
  }

  // Default tab structure
  return (
    <Toggle center={center} className={cn(center && 'my-8 md:my-14', primary ? 'top-20' : '')}>
      <ScrollView className={cn('flex px-2 pt-2.5', center && 'justify-center')}>
        {defaultValue && defaultIcon && (
          <ToggleOption
            icon={defaultIcon!}
            text={defaultValue!}
            scrollOffset={center ? 170 : adjustedOffset}
          />
        )}
        {deals ? (
          <ToggleOption
            icon={<Icon name="deal" />}
            text="Deals"
            count={deals}
            scrollOffset={center ? 170 : adjustedOffset}
          />
        ) : null}
        {comparisonCount ? (
          <ToggleOption
            icon={<Icon name="comparison" />}
            text="Comparison"
            count={comparisonCount}
            scrollOffset={center ? 170 : adjustedOffset}
          />
        ) : null}
      </ScrollView>
    </Toggle>
  )
}

export function ToggleOption({
  icon,
  text,
  count,
  action,
  scrollOffset,
  disableScroll = false,
}: Tab) {
  const { setPageTab, pageTab } = usePageContext()
  const tabRef = useRef<HTMLDivElement>(null)

  const switchTab = async (text: string) => {
    const isReselect = pageTab === text

    if (action) {
      action()
    } else {
      setPageTab(text)
    }

    tabRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })

    if (isReselect) {
      if (!disableScroll) {
        scrollToSection({ id: 'content', offset: scrollOffset ?? 45, animate: true, hideHash: true })
      }
      return
    }

    // Small delay to let the tab indicator animation complete before scrolling
    // await new Promise((resolve) => setTimeout(resolve, 400))
    if (!disableScroll) {
      scrollToSection({ id: 'content', offset: scrollOffset ?? 45, animate: true, hideHash: true })
    }
  }

  return (
    <div ref={tabRef} className="relative shrink-0 pb-2">
      <div
        onClick={() => switchTab(text)}
        className={cn(
          'flex h-8 cursor-pointer items-center gap-3 pl-2.5 pr-3 transition-colors',
          pageTab === text
            ? 'text-primary-500'
            : 'text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300',
        )}
      >
        {icon}
        <div className="flex items-center gap-2">
          {text}
          {count ? (
            <span
              className={cn(
                'flex h-5 items-center rounded-full border px-1.5 text-12 transition-colors',
                pageTab === text
                  ? 'border-primary-200 bg-primary-50 text-primary-500 dark:border-primary-500 dark:bg-primary-500/20'
                  : 'border-gray-200 bg-gray-50 text-gray-600 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400',
              )}
            >
              {count}
            </span>
          ) : null}
        </div>
      </div>
      {pageTab === text && (
        <MotionLayout>
          <m.div
            layoutId="selectedToggle"
            className="absolute bottom-0 left-3 z-10 h-0.5 w-[calc(100%-24px)] rounded-full bg-primary-500"
          />
        </MotionLayout>
      )}
    </div>
  )
}
